import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import axios from 'axios';
import { format, startOfDay, subDays } from 'date-fns';
import type { Habit } from '../pages/Habits';
import { getApiErrorMessage } from '../utils/apiError';
import { useLayoutTokens } from '../hooks/useLayoutTokens';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const HEATMAP_WEEKS = 12;

interface HabitLogResponse {
  date: string;
  completed_count: number;
}

interface HabitHistoryDialogProps {
  habit: Habit;
  open: boolean;
  onClose: () => void;
  /** Called after every successful mutation so the parent can refresh its
   * "today" summary (the +1 chip on the card, etc.). */
  onChange: () => void;
}

/** Map "YYYY-MM-DD" -> completed_count for O(1) cell lookups. */
type LogIndex = Record<string, number>;

const formatYmd = (d: Date): string => format(d, 'yyyy-MM-dd');

/** Build the 12-week heatmap grid — newest week on the right, Mondays on
 * the top row. Returns one column per week, each column containing 7
 * Date objects from Monday → Sunday. */
const buildGrid = (today: Date): Date[][] => {
  const startOfToday = startOfDay(today);
  // First, jump back to the most recent Sunday so the rightmost column
  // ends on it. The grid then walks backwards 12 weeks.
  const dayOfWeek = startOfToday.getDay(); // 0 = Sun, 1 = Mon, ...
  const daysSinceMonday = (dayOfWeek + 6) % 7; // 0 if Mon, 6 if Sun
  const thisMonday = subDays(startOfToday, daysSinceMonday);

  const cols: Date[][] = [];
  for (let w = HEATMAP_WEEKS - 1; w >= 0; w--) {
    const weekStart = subDays(thisMonday, w * 7);
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(subDays(weekStart, -d));
    }
    cols.push(week);
  }
  return cols;
};

const HabitHistoryDialog: React.FC<HabitHistoryDialogProps> = ({
  habit,
  open,
  onClose,
  onChange,
}) => {
  const theme = useTheme();
  const { dialogContentPaddingSx } = useLayoutTokens();
  const [logs, setLogs] = useState<LogIndex>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(() => startOfDay(new Date()));
  const [mutating, setMutating] = useState(false);

  const target = habit.target_count || 1;

  // Re-anchor the heatmap to "today" each time the dialog opens. Prior
  // versions used `useMemo([])` which froze the grid for the lifetime of
  // the *component*, so a long-mounted dialog showed stale weeks.
  const [gridAnchor, setGridAnchor] = useState<Date>(() => startOfDay(new Date()));
  const grid = useMemo(() => buildGrid(gridAnchor), [gridAnchor]);
  const earliestVisible = grid[0][0];
  const latestVisible = grid[grid.length - 1][6];

  /** Refetch logs for an arbitrary [start, end] window and merge into the
   * cache. Out-of-window date-picker selections call this with a tight
   * window around the selected date so the count badge / button states
   * reflect server truth. */
  const fetchRange = useCallback(
    async (start: Date, end: Date) => {
      const res = await axios.get<HabitLogResponse[]>(
        `${API_BASE_URL}/api/habits/${habit.id}/logs`,
        {
          params: {
            start_date: formatYmd(start),
            end_date: formatYmd(end),
          },
        },
      );
      const fetched: LogIndex = {};
      for (const log of res.data) {
        fetched[log.date.slice(0, 10)] = log.completed_count;
      }
      // Within [start, end] the server is authoritative — every key inside
      // the window must be set (zero-fill misses) so a previously cached
      // non-zero entry that has since been cleared doesn't ghost forever.
      const filled: LogIndex = {};
      const cursor = new Date(start);
      while (cursor.getTime() <= end.getTime()) {
        const ymd = formatYmd(cursor);
        filled[ymd] = fetched[ymd] ?? 0;
        cursor.setDate(cursor.getDate() + 1);
      }
      setLogs((prev) => ({ ...prev, ...filled }));
    },
    [habit.id],
  );

  /** Fetch the heatmap window. Used on open and after mutations. */
  const fetchHeatmapLogs = useCallback(async () => {
    try {
      setLoading(true);
      await fetchRange(earliestVisible, latestVisible);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load habit history'));
    } finally {
      setLoading(false);
    }
  }, [fetchRange, earliestVisible, latestVisible]);

  useEffect(() => {
    if (!open) return;
    // Re-anchor the grid on each open so the visible window stays current.
    setGridAnchor(startOfDay(new Date()));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    fetchHeatmapLogs();
  }, [open, fetchHeatmapLogs]);

  // When the user picks a date outside the visible heatmap, fetch a small
  // window around it so the selectedCount badge and action buttons show
  // the correct state instead of a permanent stale 0.
  const lastFetchedOutsideRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) return;
    const ymd = formatYmd(selectedDate);
    const t = selectedDate.getTime();
    const inside =
      t >= earliestVisible.getTime() && t <= latestVisible.getTime();
    if (inside) return;
    // Avoid refetching the same out-of-window day repeatedly.
    if (lastFetchedOutsideRef.current === ymd) return;
    lastFetchedOutsideRef.current = ymd;
    // Fetch the surrounding week so adjacent days light up too.
    const start = subDays(selectedDate, 3);
    const end = subDays(selectedDate, -3);
    fetchRange(start, end).catch((err) => {
      setError(getApiErrorMessage(err, 'Failed to load that day'));
    });
  }, [open, selectedDate, earliestVisible, latestVisible, fetchRange]);

  const selectedYmd = formatYmd(selectedDate);
  const selectedCount = logs[selectedYmd] ?? 0;
  const selectedFullyDone = selectedCount >= target;
  const isFutureDate = selectedDate.getTime() > Date.now();

  // -- Mutations ------------------------------------------------------------

  const runMutation = async (op: () => Promise<unknown>) => {
    try {
      setMutating(true);
      await op();
      // Refresh both the heatmap and the selected day (which may be out
      // of window). fetchHeatmapLogs only covers the visible 12 weeks so
      // we explicitly refetch the selected date on top.
      await fetchHeatmapLogs();
      const t = selectedDate.getTime();
      const insideHeatmap =
        t >= earliestVisible.getTime() && t <= latestVisible.getTime();
      if (!insideHeatmap) {
        // Reset the dedup key so the post-mutation refetch runs.
        lastFetchedOutsideRef.current = null;
        await fetchRange(subDays(selectedDate, 3), subDays(selectedDate, -3));
      }
      onChange();
    } catch (err: any) {
      if (err?.response?.status !== 404) {
        setError(getApiErrorMessage(err, 'Failed to update habit log'));
      }
    } finally {
      setMutating(false);
    }
  };

  const incrementSelected = () =>
    runMutation(() =>
      axios.post(`${API_BASE_URL}/api/habits/${habit.id}/log/increment`, {
        date_local: selectedYmd,
        amount: 1,
      }),
    );

  const completeSelected = () =>
    runMutation(() =>
      axios.post(`${API_BASE_URL}/api/habits/${habit.id}/log/complete`, {
        date_local: selectedYmd,
      }),
    );

  const clearSelected = () =>
    runMutation(() =>
      axios.delete(`${API_BASE_URL}/api/habits/${habit.id}/log`, {
        params: { date: selectedYmd },
      }),
    );

  // -- Render ---------------------------------------------------------------

  // Resolve heatmap colours from the active theme so the dialog tracks
  // light/dark mode the same as the rest of the app.
  const cellMissingColor =
    theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[200];
  const cellAccent = theme.palette.success.main;

  const cellColor = (count: number): string => {
    if (count <= 0) return cellMissingColor;
    const ratio = Math.min(1, count / target);
    // Mix between a faded accent and the full accent based on completion
    // ratio. Using rgba blending keeps it cheap and theme-aware.
    const base = theme.palette.mode === 'dark' ? 40 : 60;
    const opacity = base + Math.round(ratio * (100 - base));
    return `${cellAccent}${opacity.toString(16).padStart(2, '0')}`;
  };

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>History · {habit.title}</DialogTitle>
        <DialogContent sx={dialogContentPaddingSx}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click any day to retroactively mark, increment, or clear it. Coloured cells
            indicate completed days; intensity reflects {target > 1 ? 'progress toward target' : 'completion'}.
          </Typography>

          {/* 12-week heatmap */}
          <Box display="flex" gap={2} alignItems="flex-start" sx={{ mb: 3 }}>
            <Stack spacing={0.5} sx={{ pt: '4px' }}>
              {dayLabels.map((label) => (
                <Typography
                  key={label}
                  variant="caption"
                  color="text.secondary"
                  sx={{ height: 18, lineHeight: '18px', fontSize: 10 }}
                >
                  {label}
                </Typography>
              ))}
            </Stack>

            {loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" minHeight={140} flex={1}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box display="flex" gap={0.5}>
                {grid.map((week, wIdx) => (
                  <Stack key={wIdx} spacing={0.5}>
                    {week.map((day) => {
                      const ymd = formatYmd(day);
                      const count = logs[ymd] ?? 0;
                      const isFuture = day.getTime() > Date.now();
                      const isSelected = ymd === selectedYmd;
                      return (
                        <Tooltip
                          key={ymd}
                          title={
                            isFuture
                              ? format(day, 'PPP') + ' (future)'
                              : `${format(day, 'PPP')} · ${count}/${target}`
                          }
                          arrow
                        >
                          <Box
                            onClick={() => !isFuture && setSelectedDate(day)}
                            sx={{
                              width: 18,
                              height: 18,
                              borderRadius: '4px',
                              backgroundColor: isFuture
                                ? 'transparent'
                                : cellColor(count),
                              border: isSelected
                                ? `2px solid ${theme.palette.primary.main}`
                                : isFuture
                                ? `1px dashed ${cellMissingColor}`
                                : '1px solid transparent',
                              cursor: isFuture ? 'not-allowed' : 'pointer',
                              boxSizing: 'border-box',
                            }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Stack>
                ))}
              </Box>
            )}
          </Box>

          {/* Date picker for jumping further back than the heatmap. */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
            <DatePicker
              label="Jump to date"
              value={selectedDate}
              onChange={(value) => {
                if (value) setSelectedDate(startOfDay(value));
              }}
              disableFuture
              slotProps={{ textField: { size: 'small' } }}
            />
            <Typography variant="body2" color="text.secondary">
              Selected:&nbsp;
              <strong>{format(selectedDate, 'PPPP')}</strong> · {selectedCount}/{target}
            </Typography>
          </Box>

          {/* Selected-day actions */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {target > 1 && (
              <Button
                variant="outlined"
                startIcon={<CheckIcon />}
                disabled={mutating || isFutureDate || selectedFullyDone}
                onClick={incrementSelected}
              >
                +1 ({selectedCount}/{target})
              </Button>
            )}
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled={mutating || isFutureDate || selectedFullyDone}
              onClick={completeSelected}
            >
              Mark fully done
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<ClearIcon />}
              disabled={mutating || isFutureDate || selectedCount === 0}
              onClick={clearSelected}
            >
              Clear this day
            </Button>
            {mutating && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default HabitHistoryDialog;
